export interface SupplierReturns {
  accepting: boolean
  windowDays?: number | null
  note?: string | null
}

/**
 * Shared types between web frontend and Cloud Functions API. Mirrored
 * structurally from aska-mobile so a buyer who switches between mobile
 * and web sees the same data shape; copy here rather than depend on
 * a published package while the platform is young.
 */

export interface Diamond {
  // Stable per-stone key. stockId (TagNo) is NOT unique — one TagNo can be
  // several distinct physical stones — so uniqueId is the real doc id, used for
  // React keys, detail fetches, and cart matching. stockId stays for display.
  uniqueId?: number
  stockId: string
  refStockId: string | null
  stoneType: string
  shape: string | null
  color: string | null
  clarity: string | null
  cut: string | null
  polish: string | null
  symmetry: string | null
  fluorescence: string | null
  lab: string | null
  certNo: string | null
  carat: number
  // Null until the caller is VERIFIED (KYC-approved / admin) — signup
  // redesign 2026-08: sign-in alone no longer reveals prices.
  pricePerCarat: number | null
  priceUSD: number | null
  /** Server marker: prices were withheld for this caller (anon or unverified). */
  priceLocked?: boolean
  // baseRate / baseAmount are GONE from the wire (2026-08-08). They were the
  // supplier's raw cost, and applyMarketplace's `{...doc}` spread published
  // them to anonymous callers alongside `priceUSD: null` — the entire cost book
  // was public. The server now emits an ALLOWLISTED payload; nothing here ever
  // read these two, and re-declaring them would only invite someone to.
  /** Null for an unverified caller (see priceLocked); the favourite-supplier
   *  heart is the only consumer, and it hides itself when this is null. */
  supplierId: number | null
  /** Stone origin (from the stone's locationId/feed city+country) — e.g.
   *  "United States", "Mumbai, India". Shown to everyone (not gated). */
  origin?: string | null
  // Open-marketplace: supplier public contact (signed-in buyers only). Null in
  // broker mode.
  // `whatsapp` (2026-09-12 multi-country contacts): the number the WhatsApp
  // button opens — the supplier's line for the buyer's own country when they
  // list one, else their primary WhatsApp. Absent on an old API ⇒ callers fall
  // back to `phone` (waTargetOf in lib/contactMessage).
  supplier?: { name: string | null; phone: string | null; email: string | null; contactName?: string | null; location?: string | null; returns?: SupplierReturns | null; whatsapp?: string | null } | null
  // Output-only: how the buyer transacts this stone. Always 'contact_only' —
  // buyers deal direct (free) or via Concierge ("Buy via DiaEx"). No on-site
  // storefront checkout.
  mode?: 'contact_only'
  media: {
    imageUrl: string | null
    videoUrl: string | null
    /** Server hint: the stone has a GENUINE own photo (real image file), not a
     *  360-viewer host's blank-rendering frame path. Showcase surfaces filter on
     *  this so the landing featured row never shows a blank card. */
    hasOwnPhoto?: boolean
  }
  measurements: {
    length: number | null
    width: number | null
    height: number | null
    ratio: number | null
  }
  /** ── SUPPLIED certificate proportions (feed-written; null on the migrated
   *  catalog, which never carried them). `depthPercent != null` is the
   *  provenance signal: `specs.depthPct` is then the CERTIFICATE's number and
   *  must not be annotated as calculated. Physical spec data — not gated. */
  depthPercent?: number | null
  tablePercent?: number | null
  girdleThin?: string | null
  girdleThick?: string | null
  /**
   * Gemological proportions, computed server-side in
   * `functions/src/shared/stoneSpecs.ts` and published by applyMarketplace —
   * supplied certificate values first, derivation only where sound.
   *
   * Optional on the wire on purpose: an older cached search payload (the
   * artifact is memoized 60s) or a client running against a not-yet-deployed
   * API will not carry it. Every consumer must omit these values rather than
   * substitute its own arithmetic — the whole point of deriving them once on
   * the server is that both shells and Gemma quote the SAME number per stone.
   *
   * Not gated: every value is a pure function of `shape` + `measurements` +
   * the supplied proportions, which are already published to anonymous
   * callers, so they are identical at every trust level and disclose nothing new.
   */
  specs?: {
    /** Length-to-width ratio, trade-conventional (always ≥ 1.00), 2 dp. */
    ratio: number | null
    /** Total depth %, 1 dp. The SUPPLIED certificate value when the stone
     *  carries one (`depthPercent != null`, unclamped); otherwise derived for
     *  ROUND brilliants only (depth ÷ mean diameter). Fancy shapes without a
     *  supplied value get null — the old minor-axis derivation was provably
     *  wrong per stone (NV-8: certified 37.5% vs a derived 52.9%). */
    depthPct: number | null
    /** Table %, 1 dp — supplied only, never derivable from millimetres.
     *  Optional: absent from pre-2026-08-20 payloads. */
    tablePct?: number | null
    /** Display girdle ("Medium to Slightly Thick") — supplied only.
     *  Optional: absent from pre-2026-08-20 payloads. */
    girdle?: string | null
    /** Crown angle in degrees, 1 dp — supplied only (off the certificate),
     *  present on feed stones and null on the migrated catalog. Optional:
     *  absent from pre-2026-10-21 payloads. */
    crownAngle?: number | null
    /** Pavilion angle in degrees, 1 dp — supplied only. Optional: absent from
     *  pre-2026-10-21 payloads. */
    pavilionAngle?: number | null
    /** Whether a lab CUT grade exists for this shape at all — true for round
     *  brilliants, false for fancies. Drives whether a missing cut is a hole
     *  or simply not applicable, so no surface prints "Cut: —" on an oval. */
    cutGraded: boolean
    /**
     * The peer-band bucket this stone belongs to — `shape|colourGroup|
     * clarityGroup|caratBandIndex`, computed by `functions/src/shared/
     * peerBands.ts`. Look it up in `SearchResponse.peerBands` to position the
     * stone's $/ct against the comparable stones on Diaex.
     *
     * The KEY is unGATED for the same structural reason the three values above
     * are: it is a pure function of shape/colour/clarity/carat, all of which
     * are already on this object. The BAND it points at is an aggregate over
     * gated selling prices and arrives only for a verified caller — so an
     * unverified viewer holds a key that opens nothing, which is exactly the
     * intended amount of nothing.
     *
     * Null when any of the four dimensions is missing.
     */
    peerKey?: string | null
  }
  tripleEx: boolean
  /** Hearts & Arrows flag (#53) — set only when a feed/ERP supplies it. */
  heartsArrows?: boolean
  /** Eye-clean flag — set only when a feed/ERP supplies it (dormant like heartsArrows). */
  eyeClean?: boolean
  /** Explicitly certified free of brown/green/milky tint — feed-supplied only. */
  noBGM?: boolean
  /** Global, time-decayed engagement score (views×1 + saves×3 + contact-intent×5,
   *  ~10-day half-life). Computed in the search-index build; 0 with no recent
   *  engagement. Drives the "Most buzz" sort + "Recommended for you". */
  buzz?: number
}

export interface SearchFilters {
  shape?: string[]
  color?: string[]
  /** Substring match on color column — used for fancy hues like "yellow"
   *  that span "Fancy Vivid Yellow", "Light Yellow", etc. */
  colorLike?: string
  clarity?: string[]
  cut?: string[]
  polish?: string[]
  symmetry?: string[]
  fluorescence?: string[]
  lab?: string[]
  stoneType?: string[]
  /** Fancy-hue multi-select — OR substring match ("Yellow" hits "Fancy Vivid Yellow"). */
  fancyHues?: string[]
  /** Fancy intensity grades ("Fancy Vivid", …). Bare "Fancy" = unqualified fancies only. */
  fancyIntensities?: string[]
  /** true → only stones explicitly flagged Hearts & Arrows (#53). */
  heartsArrows?: boolean
  /** Strict opt-ins like heartsArrows — dormant until a feed supplies the flags. */
  eyeClean?: boolean
  noBGM?: boolean
  caratMin?: number | null
  caratMax?: number | null
  priceMin?: number | null
  priceMax?: number | null
  /** Price-per-carat ($/ct) range — the axis the trade compares on. */
  ppcMin?: number | null
  ppcMax?: number | null
  // Measurements (mm) + ratio (L/W). Ratio is computed server-side when the
  // stored `ratio` is absent (length/width are 100% filled).
  lengthMin?: number | null
  lengthMax?: number | null
  widthMin?: number | null
  widthMax?: number | null
  ratioMin?: number | null
  ratioMax?: number | null
  stockIds?: string[]
  /** Filter the contact-only catalog to a single supplier (merchant id = supplierId). */
  supplierId?: number | null
  /** Multi-supplier filter; with supplierExclude=true the list inverts to an exclusion.
   *  UI is KYC-gated — the supplier facet only arrives for revealed callers. */
  supplierIds?: number[]
  supplierExclude?: boolean
  /** Stone origin country (e.g. "United States", "India"). */
  country?: string[]
  /** true → country[] is an exclusion list ("Exclude location(s)"). */
  countryExclude?: boolean
  sortBy?: 'carat_asc' | 'carat_desc' | 'price_asc' | 'price_desc' | 'ppc_asc' | 'ppc_desc' | 'buzz_desc'
  limit?: number
  offset?: number
  /** Showcase surfaces only (landing featured row): server-filter to stones
   *  with a genuine own photo (media.hasOwnPhoto). */
  ownPhotoOnly?: boolean
}

export interface SearchResponse {
  total: number
  results: Diamond[]
  top3: Diamond[]
  appliedFilters: SearchFilters
  // Client-only: set by searchApi.query when the live backend was
  // unreachable and bundled demo data is being shown instead. The
  // server never sends this — it drives the "cached demo data" banner.
  isDemoFallback?: boolean
  /** Full-inventory attribute counts (chip universe), computed server-side. */
  facets?: Record<string, Record<string, number>>
  /**
   * $/ct distribution for the peer buckets the returned page touches — keyed
   * by `stone.specs.peerKey`. Computed over the WHOLE saleable catalog (not
   * this page: a 50-stone page holds ~30 buckets of one or two, which is not a
   * denominator), rebuilt with the search index every ten minutes.
   *
   * ABSENT — not empty — for an anonymous or unverified caller: it is an
   * aggregate over KYC-gated selling prices, so it follows the price gate.
   * Treat "missing" as "render no mark", never as an error.
   */
  peerBands?: Record<string, PeerBand>
}

/**
 * One peer bucket's $/ct distribution. `n` counts every stone in the bucket
 * INCLUDING the one being positioned, and the quantiles are computed over the
 * same set — see the note in `functions/src/shared/peerBands.ts` for why
 * per-stone self-exclusion buys nothing at n ≥ 9 and costs 27k sorts.
 */
export interface PeerBand {
  n: number
  p25: number
  p50: number
  p75: number
  /** Trade-language description of the bucket, e.g. "Round, G–J, VS1–VS2, 1.00–1.49ct". */
  label: string
}

export interface UserClaims {
  userId: number
  email: string
  role: 'admin' | 'supplier' | 'buyer'
  kycApproved: boolean
  // Signup wizard (2026-08) — verification + profile state for the
  // "account verifying" banner and locked-price copy.
  kycStatus?: 'not_submitted' | 'submitted' | 'approved' | 'rejected'
  kycRejectionReason?: string | null
  accountType?: 'supplier' | 'buyer' | 'broker' | null
  contactPerson?: string | null
  companyName?: string | null
  phone?: string | null
  phoneVerified?: boolean
  website?: string | null
  /** String (legacy/migrated) or the structured wizard shape. */
  address?: string | {
    line1?: string; line2?: string; city?: string; state?: string
    postalCode?: string; country?: string; countryCode?: string; countryId?: number | null
  } | null
  hearAbout?: string | null
  /** Account-creation timestamp (ISO string) for the "Member since" line.
   *  Null for older/migrated accounts with no recorded creation date — the
   *  UI omits the line in that case. */
  createdAt?: string | null
  /** First-N-free sourcing quota left (flat pricing) — server-resolved;
   *  missing on an old API reads as unknown (render nothing). */
  freeSourcesRemaining?: number
  /** PER-USER agentic-sourcing visibility — server computes `SOURCING_RFQ_LIVE
   *  armed AND (admin OR on the buyer allowlist)`. Gates whether this caller sees
   *  the ＋ / tray / My-sourcing entry points (canUseAgenticSourcing). */
  sourcingEnrolled?: boolean
  /** Org accounts (docs/ORG-ACCOUNTS.md) — the company (or individual broker)
   *  this person belongs to. ADDITIVE and null for everyone until ORGS_LIVE is
   *  armed server-side and the backfill runs; nothing in the client branches on
   *  it yet. `kycStatus`/`kycApproved` here are the ORG's, not the person's —
   *  the two are independent until org KYC lands (step 5). */
  org?: {
    id: string
    kind: 'company' | 'broker'
    legalName: string
    /** THIS user's role inside the org. */
    orgRole: 'owner' | 'admin' | 'member' | null
    kycStatus: 'not_submitted' | 'submitted' | 'approved' | 'rejected'
    kycApproved: boolean
    seats: { used: number; included: number; paid: number; limit: number }
  } | null
}

export interface PaymentTerms {
  termsId: number
  days: number
  advancePercent: number
  creditLimit: number
}

export interface CartItem {
  itemId: string
  // The owning stone's uniqueId (stringified). Present on server-returned items
  // post re-key; absent on legacy / optimistic items. Preferred over stockId
  // for inCart matching since TagNos aren't unique.
  id?: string
  stockId: string
  carat: number
  // NULL until the caller is VERIFIED (KYC-approved or admin). The cart is a
  // buyer-facing stone read and now runs through applyMarketplace exactly like
  // /search, so an unverified buyer holds items with `priceLocked: true` and
  // no prices at all. (functions/src/cart/rows.ts)
  pricePerCarat: number | null
  amount: number | null
  /** True when prices were withheld for this caller. */
  priceLocked?: boolean
  addedAt: string
  // Enriched at read time from stones/{stockId}. Masked (null) for an
  // unverified caller — same rule as the catalog.
  refStockId?: string | null
  shape?: string | null
  color?: string | null
  clarity?: string | null
  cut?: string | null
  imageUrl?: string | null
}

/** Legacy broker-mode statuses still present on historical orders. */
export type LegacyOrderStatus = 'requested' | 'confirmed' | 'rejected' | 'sold'

export interface Order {
  orderId: number
  userId: number
  orderDate: string
  /**
   * BOTH vocabularies reach the client, because /orders returns the raw doc:
   * the canonical `FulfillmentStatus` (what every Stripe-paid order lands on —
   * 'paid', 'processing', …; see functions/src/shared/orderStatus.ts) and the
   * legacy broker set. Render via lib/orderStatus.ts, never a bare lookup.
   */
  status: FulfillmentStatus | LegacyOrderStatus
  /** Sales channel: 'concierge' = Aska Services (15% fee), 'aska_plus' = Aska+
   *  membership (discounted 12% fee), 'storefront' = direct (default when absent). */
  channel?: 'storefront' | 'concierge' | 'aska_plus'
  /** Present on Aska Services orders — the admin-driven ops pipeline that the
   *  buyer timeline reads (partial: older orders predate some fields). */
  concierge?: Partial<Concierge>
  fulfillment?: Partial<Fulfillment>
  pieces: number
  amount: number
  balance: number
  paidStatus: string | null
  lines?: OrderLine[]
}

export interface OrderLine {
  orderDetailId: number
  stockId: string
  carat: number
  amount: number
  status: 'requested' | 'confirmed' | 'rejected'
}

/**
 * A company that sells on DiaEx. Mirror of functions/src/shared/types.ts —
 * the admin storefront-management surface reads/writes these. Only the
 * fields the web UI touches are typed here.
 */
export interface Merchant {
  merchantId: string
  name: string
  feePercent: number
  refundPolicy: string | null
  contact: { name: string | null; phone: string | null; email: string | null }
  stripeConnectId: string | null
  payoutsEnabled: boolean
  isPlatform: boolean
  /** DiaEx Services / Concierge QC-routing trust. */
  qcTrust?: boolean
}

/**
 * Order fulfillment lifecycle — mirror of functions/src/shared/types.ts.
 * `fulfillment` is the source of truth for where an order sits in its
 * lifecycle; the top-level order `status` is kept in sync with it.
 */
export type FulfillmentStatus =
  | 'pending_payment'
  | 'paid'
  | 'processing'
  | 'shipped'
  | 'delivered'
  | 'cancelled'
  | 'refunded'

export interface FulfillmentHistoryEntry {
  status: string
  at: string
  by: string
  note?: string | null
}

export interface Fulfillment {
  status: FulfillmentStatus
  carrier?: string | null
  trackingNumber?: string | null
  trackingUrl?: string | null
  shippedAt?: string | null
  deliveredAt?: string | null
  notes?: string | null
  history: FulfillmentHistoryEntry[]
}

/**
 * DiaEx Concierge Services (Revenue Stream 2). Mirrors the backend contract in
 * functions/src/shared/concierge.ts. Only present on channel='concierge' orders.
 */
export type QcRoute = 'direct_ship' | 'via_diaex'

export type ConciergeStage =
  | 'placed'
  | 'supplier_confirmed'
  | 'inbound'
  | 'received'
  | 'qc_passed'
  | 'qc_failed'
  | 'shipped_to_buyer'
  | 'delivered'
  | 'cancelled'

/** Which DiaEx office handles the stone: ROW → India, US stones → USA. */
export type ConciergeOffice = 'india' | 'usa'

export interface ConciergeLeg {
  kind: 'supplier_to_diaex' | 'supplier_to_buyer' | 'diaex_to_buyer'
  carrier: string | null
  trackingNumber: string | null
  trackingUrl: string | null
  office?: ConciergeOffice | null
  at: string
}

export interface ConciergeHistoryEntry {
  stage: ConciergeStage
  at: string
  by: string
  note?: string | null
}

export interface Concierge {
  qcRoute: QcRoute
  /** DiaEx office holding/handling the stone (admin-set at receive). */
  office?: ConciergeOffice | null
  /** Supplier's invoice number for this order — admin-typed. */
  invoiceNumber?: string | null
  stage: ConciergeStage
  feePercent: number
  supplierConfirmed: boolean
  supplierConfirmedAt: string | null
  destination: string | null
  legs: ConciergeLeg[]
  supplierNotifyCount: number
  qcAt: string | null
  qcResult: 'pass' | 'fail' | null
  history: ConciergeHistoryEntry[]
}

/**
 * A row from GET /admin/orders — the enriched admin-board order shape.
 * Mirrors the EnrichedOrder produced by functions/src/admin/orders.ts.
 * NOTE: the backend field is `merchantNames` (resolved supplier display
 * names), not `merchants`.
 */
export interface AdminOrderRow {
  orderId: string
  orderDate: string | null
  createdAt?: string | null
  /** `orgId`/`orgName` = which COMPANY bought this (step 8). Three colleagues
   *  on one org placing three orders is one customer, not three; the board's
   *  search matches on the org name too. Null for every pre-org account. */
  buyer: { email: string | null; name: string | null; orgId?: string | null; orgName?: string | null }
  lines: Array<{
    stockId: string | null
    shape: string | null
    carat: number | null
    amount: number | null
    merchantId: string | null
  }>
  total: number | null
  merchantNames: string[]
  status: string | null
  /** Sales channel — 'concierge' rows carry the `concierge` sub-record below. */
  channel?: 'storefront' | 'concierge'
  fulfillment: Fulfillment
  /** Present only on channel='concierge' orders — the DiaEx Services ops record. */
  concierge?: Concierge | null
  /** Commission on this concierge order (buyerPaid × feePercent). Concierge only. */
  conciergeCommission?: number | null
  /** Supplier payout on this concierge order (buyerPaid − commission). Concierge only. */
  conciergeSupplierPayout?: number | null
  /** Flat-model breakdown (order.pricing) — present only on orders placed
   *  under FLAT_PRICING_LIVE; the board labels their ledger "flat fees". */
  pricing?: { model: 'flat'; base?: number; serviceFeeUsd?: number; serviceFeeWaived?: string | null; guaranteeSelected?: boolean; guaranteeFeeUsd?: number; cardSurchargeUsd?: number; member?: boolean; total?: number } | null
  /** How the buyer chose to pay — 'bank_transfer' rows get the wire-confirm action. */
  paymentMethod?: string | null
  /** Bank-transfer sub-record (amountDue, region, confirmedAt) — wire orders only. */
  bankTransfer?: { amountDue?: number; region?: string; confirmedAt?: string | null } | null
  /** True once the post-payment loop has settled the order. */
  postPaymentDone?: boolean
}

// ─── Engineer (AI employee) — admin console "Engineer" tab ───────────────────

/** How triage classified an intake. */
export type EngineerKind = 'feature' | 'bug' | 'unknown'

/**
 * The lifecycle of an engineering request, from a rough idea to shipped.
 * draft/clarifying → still being described; approved/building/ci_running →
 * work in flight; ci_passed/deployed → done; needs_input → escalated to Tej;
 * failed → the pipeline gave up.
 */
export type EngineerStatus =
  | 'draft'
  | 'clarifying'
  | 'approved'
  | 'building'
  | 'pr_open'
  | 'ci_running'
  | 'ci_passed'
  | 'deployed'
  | 'needs_input'
  | 'failed'

/** A persisted engineering request from GET /admin/engineer/requests. */
export interface EngineeringRequest {
  id: string
  kind: EngineerKind
  rawText: string
  spec: string
  title: string
  acceptanceCriteria: string[]
  clarifyingQuestions: string[]
  status: EngineerStatus
  githubIssueNumber: number | null
  githubIssueUrl: string | null
  githubPrNumber: number | null
  githubPrUrl: string | null
  escalatedAt: string | null
  createdByUid: string
  createdAt: string | null
  updatedAt: string | null
}

/** Interactive triage result (POST /admin/engineer/triage) — does not persist. */
export interface EngineerTriageResult {
  kind: EngineerKind
  title: string
  spec: string
  clarifyingQuestions: string[]
  acceptanceCriteria: string[]
}

/** Result of the "Okay, engineer" gate (POST /admin/engineer/approve). */
export interface EngineerApproveResult {
  ok: true
  requestId: string
  issueCreated: boolean
  githubIssueUrl: string | null
  reason?: string
}

// ─── Agent Workboard — admin console "Agents" tab ────────────────────────────

/**
 * A work item's lifecycle. Agent lane: open → reviewing → planned → coding →
 * pushed → ready (for verification). Dad's lane: done / canceled / reopen.
 * needs_info = an agent left questions in the chatter; failed = pipeline
 * gave up and a human should look.
 */
export type WorkStatus =
  | 'open'
  | 'reviewing'
  | 'needs_info'
  | 'planned'
  | 'coding'
  | 'pushed'
  | 'ready'
  | 'done'
  | 'canceled'
  | 'failed'

export type WorkKind = 'bug' | 'feature' | 'chore'
export type WorkPriority = 'p0' | 'p1' | 'p2' | 'p3'

/** What the review agent concluded about a freshly filed item. */
export interface WorkItemReview {
  verdict: 'ready' | 'needs_info'
  summary: string
  questions: string[]
  areas: string[]
  constraints: string[]
}

/** One work item on the Agent Workboard (GET /admin/workboard/items). */
export interface WorkItem {
  id: string
  title: string
  description: string
  kind: WorkKind
  priority: WorkPriority
  status: WorkStatus
  review: WorkItemReview | null
  plan: string | null
  planSteps: string[]
  branch: string | null
  commits: Array<{ sha: string; message: string }>
  pushedSha: string | null
  dependsOn: string[]
  rank: number | null
  orchestratorNote: string | null
  leasedBy: string | null
  leaseUntil: string | null
  lastAgent: string | null
  error: string | null
  chatterCount: number
  createdByUid: string | null
  createdByEmail: string | null
  createdAt: string | null
  updatedAt: string | null
  closedAt: string | null
}

/** One chatter (comment-thread) message on a work item. */
export interface WorkChatterMessage {
  id: string
  from: 'admin' | 'agent' | 'orchestrator' | 'system'
  name: string
  text: string
  createdAt: string | null
}

// ─── Share-with-markup (#40) ─────────────────────────────────────────────────

/** One row on a public share page — price already marked up server-side. */
export interface ShareStone {
  displayId: string | null   // null when the sharer chose to hide the stock id
  shape: string | null
  carat: number
  color: string | null
  clarity: string | null
  cut: string | null
  polish: string | null
  symmetry: string | null
  fluorescence: string | null
  lab: string | null
  certNo: string | null
  pricePerCarat: number
  priceUSD: number
  media: { imageUrl: string | null; videoUrl: string | null }
  measurements: { length: number | null; width: number | null; height: number | null; ratio: number | null }
}

export interface ShareView {
  title: string | null
  note: string | null
  showStockId: boolean
  markupPercent: number
  markupBase?: 'supplier' | 'aska'
  createdAt: string | null
  count: number
  stones: ShareStone[]
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message)
  }
}
